import { Button, Form, Input, Modal, Popconfirm } from 'antd'
import { useDeleteUnbillablePeriod, useUnbillablePeriod, useUnbillablePeriodGpsLog, useUnbillableVisit, useUpdateUnbillablePeriod, useUpdateUnbillableVisitNotes } from '../../domain/timeTracking/timeTrackingPeriodQueries'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import { useAccount } from '../Account/accountQueries'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-duotone-svg-icons'
import GpsLog from '../maps/GpsLog'
import UnbillableForm from './UnbillableForm'

export type Props = {
  isOpen: boolean
  onCloseModal: (changesSaved: boolean) => void
  periodId: string
  googleMapsApiKey: string
}

const EditUnbillableModal = ({ isOpen, onCloseModal, periodId, googleMapsApiKey }: Props) => {
  const account = useAccount(true)
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const timeZone = account.data?.tenant.timeZoneId || browserTimezone
  const unbillablePeriodQuery = useUnbillablePeriod(periodId)
  const gpsLogQuery = useUnbillablePeriodGpsLog(periodId)
  const updateUnbillablePeriod = useUpdateUnbillablePeriod()
  const deleteUnbillablePeriod = useDeleteUnbillablePeriod()
  const unbillablePeriod = unbillablePeriodQuery.data
  const unbillableVisitQuery = useUnbillableVisit(unbillablePeriod?.unbillableVisitId)
  const updateUnbillableVisitNotes = useUpdateUnbillableVisitNotes()
  const [form] = Form.useForm()

  const gpsLog = gpsLogQuery.data ?? []

  useEffect(() => {
    if (isOpen) {
      form.resetFields()
    }
  }, [isOpen])

  useEffect(() => {
    if (unbillablePeriod) {
      form.setFieldsValue({
        startUtc: dayjs(unbillablePeriod.startUtc).tz(timeZone),
        endUtc: unbillablePeriod.endUtc ? dayjs(unbillablePeriod.endUtc).tz(timeZone) : undefined,
        unbillableCategoryId: unbillablePeriod.unbillableCategoryId,
        lunch: (unbillablePeriod.lunch ? Math.round(unbillablePeriod.lunch * 60) : undefined),
      })
    }
  }, [unbillablePeriod, isOpen, timeZone, unbillableVisitQuery.data])
  
  useEffect(() => {
    if (unbillableVisitQuery.data) {
      form.setFieldsValue({ notes: unbillableVisitQuery.data.notes })
    }
  }, [unbillableVisitQuery.data?.notes, isOpen])

  const handleSubmit = async (submit: boolean) => {
    try {
      const values = await form.validateFields()
      if (!unbillablePeriod) {
        return
      }

      const lunch = values.lunch ? values.lunch / 60 : 0
      updateUnbillablePeriod.mutate({ period: { ...unbillablePeriod, ...values, lunch }, submit }, { onSuccess: () => onCloseModal(true) })

      if (values.notes && values.notes !== unbillableVisitQuery.data?.notes && unbillablePeriod.unbillableVisitId) {
        updateUnbillableVisitNotes.mutate({ visitId: unbillablePeriod.unbillableVisitId, notes: values.notes })
      }
    } catch { }
  }

  const handleDelete = () => {
    deleteUnbillablePeriod.mutate(periodId, { onSuccess: () => onCloseModal(true) })
    onCloseModal(true)
  }

  const handleCancel = () => {
    form.resetFields()
    onCloseModal(false)
  }

  return <Modal
    open={isOpen}
    onCancel={handleCancel}
    title="Edit Unbillable Time"
    onOk={() => handleSubmit(false)}
    footer={[
      <Popconfirm key="delete" title="Are you sure you want to delete this unbillable time?" onConfirm={handleDelete}>
        <Button type="primary" danger ghost><FontAwesomeIcon icon={faTrash} /></Button>
      </Popconfirm>,
      <Button key="cancel" onClick={handleCancel}>Discard Changes</Button>,
      <Button key="save" type="primary" loading={updateUnbillablePeriod.isLoading} onClick={() => handleSubmit(false)}>Save</Button>,
      <Button key="saveAndSubmit" type="primary" loading={updateUnbillablePeriod.isLoading} onClick={() => handleSubmit(true)}>Save and Submit</Button>,
    ]}
    width={gpsLog.length > 0 ? 900 : undefined}
  >
    <Form form={form} layout="vertical">
      <UnbillableForm form={form} timeZone={timeZone} />
      <Form.Item label="Notes" name="notes">
        <Input.TextArea rows={4} />
      </Form.Item>
    </Form>
    {gpsLog.length > 0 &&
      <GpsLog googleMapsApiKey={googleMapsApiKey} logs={gpsLog} hideTitle />
    }
  </Modal>
}

export default EditUnbillableModal
